// src/hooks/useProviderResolution.js
// Resolves the correct provider_id for encounter/note creation.
// Cascade: self (if independent) → appointment provider → supervisor → fallback to self.

import { useState, useEffect, useCallback } from 'react';
import { useUser } from '@/contexts/UserContext';
import { apiService } from '@/services/api';

export function useProviderResolution({ patient, appointment }) {
  const { user } = useUser();
  const [resolvedProviderId, setResolvedProviderId] = useState(user?.id);
  const [resolving, setResolving] = useState(false);
  const [resolutionSource, setResolutionSource] = useState('self');
  const [availableSupervisors, setAvailableSupervisors] = useState([]);

  useEffect(() => {
    if (!user?.id) return;

    // Step 1: Independent provider — always use self, regardless of whose
    // calendar the appointment is on. No supervision applies.
    if (user.supervision_level !== 'supervised' && user.supervision_level !== 'student') {
      setResolvedProviderId(user.id);
      setResolutionSource('self');
      setResolving(false);
      return;
    }

    // Step 2: Supervised/student provider starting another provider's appointment —
    // that provider is their supervisor for this visit.
    if (appointment?.provider_id && appointment.provider_id !== user.id) {
      setResolvedProviderId(appointment.provider_id);
      setResolutionSource('appointment');
      setResolving(false);
      return;
    }

    // Step 3: Supervised provider — resolve active supervision relationships
    let cancelled = false;
    const resolve = async () => {
      setResolving(true);
      try {
        // Care team membership does not establish a supervision relationship.
        const relationships = await apiService.getSupervisionRelationships({
          supervisee_id: user.id,
          is_active: true,
        });
        if (!cancelled && relationships?.length > 0) {
          setAvailableSupervisors(relationships);
          setResolvedProviderId(relationships[0].supervisor_id);
          setResolutionSource('supervisor');
          setResolving(false);
          return;
        }
      } catch (err) {
        console.error('[useProviderResolution] Error resolving provider:', err);
      }

      // Step 4: Fallback to self
      if (!cancelled) {
        setResolvedProviderId(user.id);
        setResolutionSource('self');
        setResolving(false);
      }
    };

    resolve();
    return () => { cancelled = true; };
  }, [user?.id, user?.supervision_level, patient?.id, appointment?.id, appointment?.provider_id]);

  const selectSupervisor = useCallback((supervisorId) => {
    setResolvedProviderId(supervisorId);
  }, []);

  return { resolvedProviderId, resolving, resolutionSource, availableSupervisors, selectSupervisor };
}
